import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Box,
  IconButton,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLazyGetUserTransactionsQuery } from "../../services/user";
import type {
  TransactionItem,
  TransactionListData,
  UserTransactionsQueryParams,
} from "../../types/Transaction";
import { useParams } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { isValidInput } from "../../utils/validations";
import { showError } from "../../helpers";
import Pagination from "../../components/Panigation";

interface TransactionHistoryProps {
  hideUserColumn?: boolean;
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  hideUserColumn,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [UserRole, setUserRole] = React.useState("default");
  const handleUserRoleChange = (event: SelectChangeEvent) => {
    setUserRole(event.target.value as string);
  };
  const [ActionType, setActionType] = React.useState("default");
  const handleActionTypeChange = (event: SelectChangeEvent) => {
    setActionType(event.target.value as string);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (isValidInput(value)) {
      setSearchTerm(value);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  };

  const handleDateChange = (value: Dayjs | null) => {
    setSelectedDate(value);
  };

  const { dateFrom, dateTo } = useMemo(() => {
    if (!selectedDate) {
      return { dateFrom: undefined, dateTo: undefined };
    }
    // Format date as YYYY-MM-DD and append UTC time to avoid timezone shifts
    const dateStr = selectedDate.format("YYYY-MM-DD");
    return {
      dateFrom: selectedDate, //`${dateStr}T00:00:00.000Z`,
      dateTo: selectedDate, //`${dateStr}T23:59:59.999Z`,
    };
  }, [selectedDate]);

  const [totalCount, setTotalCount] = useState(0);
  const [userTransactions, setUserTransactions] = useState<TransactionListData>(
    { list: [], totalCount: 0 }
  );
  const [getTransactionsData] = useLazyGetUserTransactionsQuery();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || 0) / (pageSize || 10))
  );

  const fetchList = useCallback(async () => {
    if (!id) {
      setUserTransactions({ list: [], totalCount: 0 });
      setTotalCount(0);
      return;
    }
    try {
      const params: UserTransactionsQueryParams = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm || undefined,
        dateFrom: selectedDate
          ? dayjs(selectedDate).utc().format("YYYY-MM-DD") + "T00:00:00.000Z"
          : undefined,
        dateTo: selectedDate
          ? dayjs(selectedDate).utc().format("YYYY-MM-DD") + "T23:59:59.999Z"
          : undefined,
        id: id as string,
      };
      const response = await getTransactionsData(params).unwrap();
      if (response?.statusCode === 200) {
        const normalizedList =
          response?.data?.transactionData || response?.data?.list || [];
        const normalizedTotal =
          response?.data?.count ||
          response?.data?.totalCount ||
          normalizedList.length ||
          0;
        setUserTransactions({
          list: normalizedList,
          totalCount: normalizedTotal,
        });
        setTotalCount(normalizedTotal);
      }
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data
        ?.message;
      showError(apiMessage || "Failed to fetch transactions");
    }
  }, [
    id,
    page,
    pageSize,
    debouncedSearchTerm,
    dateFrom,
    dateTo,
    getTransactionsData,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, dateFrom, dateTo]);
  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            searchTerm={searchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            onCross={handleClearSearch}
          />
          <div className="form">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                className="form_control"
                value={selectedDate}
                onChange={handleDateChange}
                slots={{
                  openPickerIcon: () => (
                    <img src="/static/images/date_icon.svg" alt="Pick date" />
                  ),
                }}
                slotProps={{
                  textField: {
                    id: "transaction-date",
                    placeholder: "Select Date",
                    fullWidth: true,
                    hiddenLabel: true,
                  },
                  actionBar: {
                    actions: ["clear"],
                  },
                }}
                desktopModeMediaQuery="(min-width:0px)"
              />
            </LocalizationProvider>
          </div>
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Receipt ID</TableCell>
              <TableCell>Transaction Date</TableCell>
              {!hideUserColumn && <TableCell>User</TableCell>}
              <TableCell>Location</TableCell>
              <TableCell>Points Redeemable From</TableCell>
              <TableCell>Purchase Date</TableCell>
              <TableCell>Expire Date</TableCell>
              <TableCell>Entry Type</TableCell>
              <TableCell>Points</TableCell>
              <TableCell>Source Type</TableCell>
              <TableCell>Post Terminal</TableCell>
              {/* <TableCell>Actions</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {(userTransactions?.list || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={hideUserColumn ? 8 : 9} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              (userTransactions?.list || []).map((tx: TransactionItem) => {
                const entryType = String(tx?.entryType || "").toLowerCase();
                const basePoints = entryType.includes("expire")
                  ? tx?.expiredPoints || tx?.points || 0
                  : tx?.points || 0;
                const isPositive =
                  entryType.includes("sale") ||
                  entryType.includes("earn") ||
                  entryType.includes("positive");
                const signed = isPositive
                  ? Math.abs(basePoints)
                  : -Math.abs(basePoints);
                const user = tx?.userDetails || {};
                return (
                  <TableRow key={tx?._id}>
                    <TableCell>{tx?.receiptNo || "-"}</TableCell>

                    <TableCell>
                      {tx?.createdAt
                        ? new Date(tx.createdAt).toLocaleString()
                        : "-"}
                    </TableCell>
                    {!hideUserColumn && (
                      <TableCell>
                        <Box
                          component="a"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate("/manage-users/detail", {
                              state: { id: tx?.userId },
                            });
                          }}
                          className="user_block click_action"
                        >
                          <b>{user?.fullName || "-"}</b>
                          <span>
                            {`${user?.dialCode || ""} ${
                              user?.phoneNo || ""
                            }`.trim()}
                          </span>
                          <span>{user?.email || ""}</span>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.location || "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.pointsRedeemableFrom
                        ? new Date(tx.pointsRedeemableFrom).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.purchaseDate
                        ? new Date(tx.purchaseDate).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.expires && tx.expires[0]
                        ? new Date(tx.expires[0]).toLocaleString()
                        : "-"}
                    </TableCell>

                    <TableCell>{tx?.entryType || "-"}</TableCell>
                    <TableCell>
                      <p
                        className={`status_btn ${
                          signed > 0 ? "c_success" : "c_danger"
                        }`}
                      >
                        {`${signed > 0 ? "+" : ""}${signed} pts`}
                      </p>
                    </TableCell>
                    <TableCell>{tx?.sourceType || "-"}</TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.posTerminal || "-"}
                    </TableCell>
                    {/* <TableCell>
                    <div className="table_actions v2">
                      <Box>
                        <IconButton
                          onClick={() => navigate("/manage-transactions/view", { state: { id: tx?._id } })}>
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                      </Box>
                    </div>
                  </TableCell> */}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        module={userTransactions?.list || []}
        page={page}
        setPage={setPage}
        limit={pageSize}
        setLimit={setPageSize}
        onPageChange={onPageChange}
        totalPages={totalPages}
      />
    </>
  );
};

export default TransactionHistory;
